import { memo, useEffect, useMemo, useRef } from 'react';
import { Group, Line, Transformer, Circle, Path } from 'react-konva';
import Konva from 'konva';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import { useRefs } from 'contexts/RefContext';
import { setNewlyAddedPanel, setTempPanelsGroup } from 'store/slices/PanelSlice';

function TempRectGroup({ roofSegmentId }: { roofSegmentId: string }) {
	const { tempPanelsGroup } = useSelector((state: RootState) => state.panelSlice.data);
	const { konvaRef } = useRefs();
	const scale = useKonvaImageScale();
	const dispatch = useDispatch();

	const groupRef = useRef<Konva.Group>(null);
	const trRef = useRef<Konva.Transformer>(null);
	const buttonsGroupRef = useRef<Konva.Group>(null);

	const panels = useMemo(
		() => tempPanelsGroup.filter(p => p.roofSegmentId === roofSegmentId),
		[tempPanelsGroup, roofSegmentId]
	);

	useEffect(() => {
		if (trRef.current && groupRef.current) {
			trRef.current.nodes([groupRef.current]);
			trRef.current.getLayer()?.batchDraw();
			updateButtonsPosition();

			groupRef.current.moveToTop();
			trRef.current.moveToTop();
			buttonsGroupRef.current?.moveToTop();
		}
	}, [panels]);

	useEffect(() => {
		const stage = konvaRef?.current;
		if (!stage) return;
	
		const observer = new ResizeObserver(() => {
			updateButtonsPosition();
		});
		observer.observe(stage.container());
	
		return () => observer.disconnect();
	}, []);

	const updateButtonsPosition = () => {
		const groupNode = groupRef.current;
		const stage = konvaRef?.current;
		if (!groupNode || !stage) return;
	
		const box = groupNode.getClientRect();
	
		const bottomCenter = {
			x: box.x + box.width / 2,
			y: box.y + box.height + 25,
		};
	
		const stageTransform = stage.getAbsoluteTransform().copy().invert();
		const position = stageTransform.point(bottomCenter);
	
		buttonsGroupRef.current?.position(position);
		buttonsGroupRef.current?.getLayer()?.batchDraw();
	};

	const handleTransformOrDrag = () => {
		const group = groupRef.current;
		if (!group) return;

		const dx = group.x() / scale.x;
		const dy = group.y() / scale.y;

		const updatedPanels = panels.map(panel => ({
			...panel,
			exteriorCoords: panel.exteriorCoords.map(([x, y]) => [x + dx, y + dy]),
		}));

		dispatch(setTempPanelsGroup({ addedPanels: updatedPanels }));
		group.position({ x: 0, y: 0 });
		group.getLayer()?.batchDraw();
		updateButtonsPosition();
	};

	const handleMoveButtonDragMove = (e: any) => {
		const { x, y } = e.target.position();
		if (groupRef.current) {
			groupRef.current.position({ x, y });
			handleTransformOrDrag();
		}
		e.target.position({ x: 0, y: 0 });
	};

	const handleDoneButton = () => {
		if(!konvaRef?.current) return null;
		const container = konvaRef.current?.container();
		if (container) {
			container.style.cursor = 'default';
		}
		dispatch(setNewlyAddedPanel({ addedPanels: tempPanelsGroup }));
		dispatch(setTempPanelsGroup({ addedPanels: [] }));
	};

	const renderButton = (iconPath: string, draggable = false) => (
		<Group
			draggable={draggable}
			cursor = {'move'}
			onMouseEnter={(e) => {
				const container = e.target.getStage()?.container();
				if (container) container.style.cursor = draggable ? 'move' :'pointer';
			}}
			onMouseLeave={(e) => {
				const container = e.target.getStage()?.container();
				if (container) container.style.cursor = 'default';
			}}
		>
			<Circle radius={12.5} fill="#fff" />
			<Path
				data={iconPath}
				stroke="#003CFF"
				strokeWidth={1}
				lineCap="round"
				lineJoin="round"
				x={-6}
				y={-6}
			/>
		</Group>
	);

	return (
		<>
			<Group
				ref={groupRef}
				draggable
				scale={scale}
				onDragMove={updateButtonsPosition}
				onDragEnd={handleTransformOrDrag}
			>
				{panels.map((panel, index) => (
					<Line
						key={`${panel.roofSegmentId}-${index}`}
						points={panel.exteriorCoords.flat()}
						stroke="rgba(0, 128, 255, 0.5)"
						strokeWidth={1.4}
						closed
					/>
				))}
			</Group>

			{panels.length > 0 && (
				<>
					<Transformer
						ref={trRef}
						rotateEnabled={false}
						resizeEnabled={false}
						enabledAnchors={[]}
						borderStroke="rgba(0,128,255,0.6)"
						borderStrokeWidth={5}
						anchorStroke="transparent"
					/>

					<Group
						ref={buttonsGroupRef}
						scale={scale}
					>
						<Group x={-15} 
							onDragMove={handleMoveButtonDragMove}>
							{renderButton(
								'M7.7 2.2L6.5 1 5.3 2.2M6.5 1V12M6.5 12L7.7 10.8M6.5 12L5.3 10.8M10.8 7.7L12 6.5 10.8 5.3M12 6.5H1M1 6.5L2.2 7.7M1 6.5L2.2 5.3',
								true				
							)}
						</Group>

						<Group x={15} onPointerClick={handleDoneButton}>
							{renderButton('M1 7L5 10L12.5 1', false)}
						</Group>
					</Group>
				</>
			)}
		</>
	);
}

export default memo(TempRectGroup);
